import { Locale, LOCALES, toLocale } from '@longy/common'
import { toCanonicalPath } from '@longy/common/cms'
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@longy/web-components'
import { useQueryClient } from '@tanstack/react-query'
import { useMatch, useNavigate } from '@tanstack/react-router'
import i18next, { changeLanguage } from 'i18next'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { api } from '../core/trpc'
import { useAuth } from '../hooks/useAuth'
import { LocaleFlag } from './LocaleFlag'

/** The key `resolveInitialLocale` reads, so the switcher and the next page load agree. */
const LOCALE_STORAGE_KEY = 'i18nextLng'

/**
 * The language control in the header: a flag, and nothing else.
 *
 * Deliberately not the shared `LanguageSelect` — that one is a full-width `Select` spelling out
 * "Magyar", and admin's `MainLayout` still wants exactly that. So this app owns its own compact
 * control, and the shared component is left alone.
 *
 * Changing the language is more than `changeLanguage`. Every DB-backed payload (service names, web
 * content, translations) is localised server-side from the `x-locale` header, so the cache has to be
 * dropped or the page keeps showing the previous language's text.
 */
export const LanguageSwitcher = () => {
    const { t, i18n } = useTranslation()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const { isAuthenticated } = useAuth()
    const { mutate: changePreferredLanguage } = api.user.changePreferredLanguage.useMutation()

    /**
     * A cms page's language is its url — `/blokk-katalogus` is the Hungarian page and
     * `/block-gallery` the English one — so changing the interface language has to move the
     * visitor to the other url as well, or they are left reading the language they just switched
     * away from under chrome that changed around them.
     *
     * `shouldThrow: false` is what lets the same header sit on the 36 fixed routes, whose urls are
     * not per-locale and have nowhere to go.
     */
    const localizedSlugs = useMatch({
        from: '/$',
        shouldThrow: false,
        select: (match) => match.loaderData?.page?.localizedSlugs,
    })

    // The resource store is read below. The root loader swallows a failed translation fetch, so
    // i18n may never have been initialised — render nothing rather than taking the header down.
    if (!i18next.isInitialized || !i18next.services?.resourceStore?.data) return null

    const currentLocale = toLocale(i18n.language)

    const onSelect = (locale: Locale) => {
        void changeLanguage(locale)

        try {
            localStorage.setItem(LOCALE_STORAGE_KEY, locale)
        } catch {
            // Private browsing and blocked site data throw on write. The language still changes for
            // this visit; only the memory of it is lost.
        }

        queryClient.invalidateQueries()
        if (isAuthenticated) changePreferredLanguage({ locale })

        // Only when this page has that translation. Without one there is no url to move to, and
        // staying put beats sending the visitor to a 404.
        const slug = localizedSlugs?.[locale]
        if (slug !== undefined) void navigate({ to: toCanonicalPath(`/${slug}`) })
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 px-2"
                    aria-label={t('frontend.header.language')}
                >
                    <LocaleFlag locale={currentLocale} />
                    <ChevronDown className="size-3 text-brand-gray" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-0">
                {LOCALES.map((locale) => (
                    <DropdownMenuItem
                        key={locale}
                        className="gap-2"
                        onSelect={() => onSelect(locale)}
                    >
                        <LocaleFlag locale={locale} />
                        <span className="text-sm font-medium">{locale.toUpperCase()}</span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
